import { S3, config as awsConfig } from "aws-sdk";
import { ServerConfig } from "../connectors/Config";

/**
 * Real S3 uploader for the M.A.S.E. ecosystem.
 *
 * Replaces the previous mock (which returned a temporary local object URL).
 * Uploads the given file to the configured S3 bucket and returns the public URL.
 *
 * Configuration is taken from environment variables via ServerConfig
 * (see connectors/Config.js and .env.example):
 *   - REACT_APP_AWS_REGION
 *   - REACT_APP_S3_BUCKET
 *   - REACT_APP_S3_URL
 *   - REACT_APP_AWS_ACCESS_KEY_ID
 *   - REACT_APP_AWS_SECRET_ACCESS_KEY
 *
 * @param {File} file - the file selected by the user
 * @param {Object} [options]
 * @param {string} [options.prefix="document-file"] - logical tag used in the key
 * @param {number} [options.maxSizeMB=10] - max allowed size in MB
 * @param {string[]} [options.allowedTypes] - allowed MIME types (default: any)
 * @returns {Promise<string>} the public URL of the uploaded object
 */
export async function uploadFileToS3(file, options = {}) {
  const {
    prefix = "document-file",
    maxSizeMB = 10,
    allowedTypes = null,
  } = options;

  // --- Validation ---
  if (!file) {
    throw new Error("Nessun file fornito per l'upload.");
  }

  const { Region, BucketName, ApiVersion, AccessKeyId, SecretKeyId } =
    ServerConfig.AWS.s3Bucket;

  if (!BucketName) {
    throw new Error(
      "Bucket S3 non configurato (REACT_APP_S3_BUCKET mancante)."
    );
  }
  if (!AccessKeyId || !SecretKeyId) {
    throw new Error(
      "Credenziali AWS S3 non configurate (REACT_APP_AWS_ACCESS_KEY_ID / REACT_APP_AWS_SECRET_ACCESS_KEY mancanti)."
    );
  }

  if (typeof maxSizeMB === "number" && file.size > maxSizeMB * 1024 * 1024) {
    throw new Error(
      `File troppo grande: massimo ${maxSizeMB} MB (dimensione: ${(
        file.size /
        (1024 * 1024)
      ).toFixed(2)} MB).`
    );
  }

  if (Array.isArray(allowedTypes) && allowedTypes.length > 0) {
    if (!allowedTypes.includes(file.type)) {
      throw new Error(
        `Tipo di file non consentito: "${file.type}". Consentiti: ${allowedTypes.join(", ")}.`
      );
    }
  }

  // --- AWS SDK configuration (credentials from env via ServerConfig) ---
  const credentials = {
    accessKeyId: AccessKeyId,
    secretAccessKey: SecretKeyId,
  };

  awsConfig.update({ region: Region, credentials });

  const s3 = new S3({
    apiVersion: ApiVersion,
    region: Region,
    credentials,
  });

  // --- Build a unique key, preserving the original extension ---
  const extFromName = file.name && file.name.includes(".")
    ? file.name.split(".").pop().toLowerCase()
    : "";
  const extFromType = file.type ? file.type.split("/")[1] : "";
  const ext = extFromName || extFromType || "bin";
  const key = `${Date.now()}.${prefix}.${ext}`;

  // --- Read file as ArrayBuffer (browser-safe) ---
  const arrayBuffer = await file.arrayBuffer();
  const body = new Uint8Array(arrayBuffer);

  const params = {
    Bucket: BucketName,
    Key: key,
    Body: body,
    ContentType: file.type || "application/octet-stream",
    ACL: "public-read",
  };

  // --- Upload ---
  try {
    await s3.putObject(params).promise();
  } catch (err) {
    // Surface a clear, localized error while keeping the original cause.
    const reason = err && err.message ? err.message : String(err);
    throw new Error(`Errore durante l'upload su S3: ${reason}`);
  }

  // --- Return the public URL ---
  const baseUrl =
    ServerConfig.AWS.S3.URL ||
    `https://${BucketName}.s3.${Region}.amazonaws.com`;
  return `${baseUrl.replace(/\/+$/, "")}/${key}`;
}
